import React, { useRef, useEffect, useState } from 'react';
import { Text, Stack, Button, Constrain } from '@moda/om';
import { useParams } from 'react-router-dom';
import { breakpoints } from '@moda/tokens';
import { remToUnitlessPx } from '../../lib/remToUnitlessPx';
import { hrefFor } from '../../routers';
import { CarouselPumoModuleGridFragment_cells_Look as LookCell } from '../../generated/types/CarouselPumoModuleGridFragment';
import { useSwiper } from '../../components/Swiper';
import { LookCellsGridContent } from './LookCellsGridContent';
import { getLookCellsGridScenario, LookCellsGridScenario } from './getLookCellsGridScenario';

import './LookCellsGrid.scss';

export interface LookCellsGridProps {
  looks: LookCell[];
  galleryViewUrl?: string;
}

const getSlidesPerColumn = (scenario: string) => {
  if (scenario === 'divisiblebytwo') {
    return 2;
  }
  if (scenario === 'divisiblebythree') {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    return 3;
  }
  return 1;
};

export const LookCellsGrid: React.FC<LookCellsGridProps> = ({ looks }) => {
  const { scenario } = getLookCellsGridScenario(looks.length);

  const ref = useRef<HTMLDivElement>(null);

  const { vertical = 'women' } = useParams<{ vertical?: string }>();

  const firstLook = looks[0];
  const href =
    firstLook && firstLook.trunkshowSlugs?.length
      ? hrefFor.LookviewPage({
          vertical: vertical,
          trunkshowSlug: `${firstLook.trunkshowSlugs[0]}`,
          trunkshowId: `${firstLook.trunkshowId}`,
          lookId: firstLook.id
        })
      : undefined;

  const [scrollable, setScrollable] = useState(false);

  useSwiper('.LookCellsGrid__carousel', {
    slidesPerView: 4,
    spaceBetween: 8,
    slidesPerColumn: getSlidesPerColumn(LookCellsGridScenario[scenario].toLowerCase()),
    slidesPerColumnFill: 'row',
    grabCursor: true,
    scrollbar: {
      el: '.LookCellsGrid__scrollbar',
      draggable: true,
      dragClass: 'LookCellsGrid__scrollbar-drag'
    },
    breakpoints: {
      [remToUnitlessPx(breakpoints.xs)]: {
        slidesPerView: 5,
        slidesPerColumn: getSlidesPerColumn(LookCellsGridScenario[scenario].toLowerCase())
      },
      [remToUnitlessPx(breakpoints.sm)]: {
        slidesPerView: 8,
        slidesPerColumn: getSlidesPerColumn(LookCellsGridScenario[scenario].toLowerCase())
      },
      [remToUnitlessPx(breakpoints.md)]: {
        slidesPerView: 9,
        slidesPerColumn: getSlidesPerColumn(LookCellsGridScenario[scenario].toLowerCase())
      },
      [remToUnitlessPx(breakpoints.lg)]: {
        slidesPerView: 10,
        slidesPerColumn: getSlidesPerColumn(LookCellsGridScenario[scenario].toLowerCase())
      }
    }
  });

  useEffect(() => {
    if (ref.current) {
      if (ref.current.clientWidth < ref.current.scrollWidth) {
        setScrollable(true);
      }
    }
  }, [scrollable]);

  return (
    <Stack space={3}>
      <div className="LookCellsGrid__content-wrapper LookCellsGrid__carousel" ref={ref}>
        <LookCellsGridContent looks={looks} galleryViewUrl={href} />
        <div className="LookCellsGrid__scrollbar" />
      </div>

      <Constrain>
        <Stack space={3}>
          {scrollable && <Text treatment="body1"> {looks.length} looks</Text>}

          <div className="LookCellsGrid__shop-by-look">
            <Button secondary to={href}>
              Shop by Look
            </Button>
          </div>
        </Stack>
      </Constrain>
    </Stack>
  );
};
